/**
 * Presences — suivi des présences pastorales avec 3 onglets :
 *  1. Signaler les absents (recherche rapide + bulk)
 *  2. Marquer par cellule
 *  3. Alertes pastorales (rouge / orange / jaune)
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import Fuse from "fuse.js";
import { useNavigate } from "react-router-dom";
import { colors, fonts, goldGradient } from "../styles/tokens";
import { presencesApi, evenementsApi, cellulesApi } from "../services/api";
import useMembers from "../hooks/useMembers";
import useToast from "../hooks/useToast";
import Card from "../components/Card";
import Button from "../components/Button";
import Input from "../components/Input";
import Select from "../components/Select";
import Badge from "../components/Badge";
import Avatar from "../components/Avatar";
import CelluleForm from "../components/CelluleForm";

const TABS = [
  { id: "signaler", label: "Signaler les absents", icon: "🔍" },
  { id: "cellules", label: "Par cellule",          icon: "👥" },
  { id: "alertes",  label: "Alertes pastorales",   icon: "⚠" },
];

export default function Presences() {
  const [active, setActive] = useState("signaler");

  return (
    <div style={{ fontFamily: fonts.body, color: colors.text }}>
      <header style={{ marginBottom: "24px" }}>
        <h1 style={{ fontFamily: fonts.heading, fontSize: "36px", fontWeight: 700, marginBottom: "4px" }}>
          Présences
        </h1>
        <p style={{ color: colors.textMuted, fontSize: "14px" }}>
          Suivi pastoral et signalement des absents
        </p>
      </header>

      {/* Onglets */}
      <div style={{
        display: "flex", gap: "4px", flexWrap: "wrap",
        background: colors.card, padding: "6px", borderRadius: "12px",
        border: `1px solid ${colors.border}`,
        marginBottom: "20px",
      }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setActive(t.id)}
            style={{
              flex: "1 1 auto",
              minWidth: 140,
              padding: "10px 16px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: active === t.id ? 600 : 500,
              color: active === t.id ? colors.bg : colors.text,
              background: active === t.id ? goldGradient : "transparent",
              transition: "all 200ms",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <span>{t.icon}</span>
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {active === "signaler" && <TabSignaler />}
      {active === "cellules" && <TabCellules />}
      {active === "alertes"  && <TabAlertes />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// ONGLET 1 — SIGNALER LES ABSENTS
// ════════════════════════════════════════════════════════════

function TabSignaler() {
  const { members, eglises } = useMembers();
  const toast = useToast();

  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [egliseId, setEgliseId] = useState("");
  const [evenementId, setEvenementId] = useState("");
  const [evenements, setEvenements] = useState([]);
  const [search, setSearch] = useState("");
  const [absents, setAbsents] = useState([]);   // [{id, prenom, nom, ...}]
  const [submitting, setSubmitting] = useState(false);

  // Charge les événements de l'église sélectionnée
  useEffect(() => {
    if (!egliseId) {
      setEvenements([]);
      return;
    }
    evenementsApi.list({ eglise_id: egliseId })
      .then(({ data }) => setEvenements(data || []))
      .catch(() => setEvenements([]));
  }, [egliseId]);

  // Membres de l'église choisie (sinon tous)
  const eligibleMembers = useMemo(() => {
    if (!egliseId) return [];
    return members.filter((m) => m.eglise_id === Number(egliseId));
  }, [members, egliseId]);

  // Index Fuse pour recherche fuzzy
  const fuse = useMemo(
    () => new Fuse(eligibleMembers, {
      keys: ["prenom", "nom", "full_name", "email", "telephone"],
      threshold: 0.35,
      ignoreLocation: true,
      minMatchCharLength: 2,
    }),
    [eligibleMembers]
  );

  const searchResults = useMemo(() => {
    if (search.trim().length < 2) return [];
    const absentIds = new Set(absents.map((a) => a.id));
    return fuse.search(search.trim())
      .map((r) => r.item)
      .filter((m) => !absentIds.has(m.id))
      .slice(0, 8);
  }, [search, fuse, absents]);

  const addAbsent = (member) => {
    setAbsents((prev) => [...prev, member]);
    setSearch("");
  };

  const removeAbsent = (id) => {
    setAbsents((prev) => prev.filter((m) => m.id !== id));
  };

  const handleSubmit = async () => {
    if (!egliseId) { toast.error("Veuillez sélectionner une église"); return; }
    if (!date)     { toast.error("Veuillez sélectionner une date");   return; }
    if (absents.length === 0) {
      toast.info("Aucun absent à signaler");
      return;
    }

    setSubmitting(true);
    try {
      await presencesApi.signalerAbsences({
        date_culte: date,
        eglise_id: Number(egliseId),
        evenement_id: evenementId ? Number(evenementId) : null,
        membres_absents: absents.map((m) => m.id),
      });
      toast.success(`${absents.length} absent${absents.length > 1 ? "s" : ""} signalé${absents.length > 1 ? "s" : ""}`);
      setAbsents([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du signalement");
    } finally {
      setSubmitting(false);
    }
  };

  const eglisesOptions = eglises.map((e) => ({ value: String(e.id), label: e.nom }));
  const evenementsOptions = evenements.map((e) => ({
    value: String(e.id),
    label: `${e.titre} — ${new Date(e.date_debut).toLocaleDateString("fr-FR")}`,
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* Sélecteurs */}
      <Card>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
          <Select
            label="Église" required value={egliseId} onChange={setEgliseId}
            options={eglisesOptions} placeholder="Sélectionnez une église..."
          />
          <Input
            label="Date du culte" type="date" required
            value={date} onChange={(e) => setDate(e.target.value)}
          />
          <Select
            label="Événement (optionnel)" value={evenementId} onChange={setEvenementId}
            options={evenementsOptions}
            placeholder={evenements.length ? "Aucun" : "—"}
          />
        </div>
      </Card>

      {/* Recherche */}
      {egliseId && (
        <Card>
          <div style={{ position: "relative" }}>
            <Input
              label={`Rechercher un membre absent (${eligibleMembers.length} membres)`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tapez le prénom ou le nom…"
              icon="🔍"
              autoFocus
            />
            {/* Résultats live */}
            {searchResults.length > 0 && (
              <div style={{
                marginTop: "10px",
                background: colors.bg,
                border: `1px solid ${colors.border}`,
                borderRadius: "10px",
                overflow: "hidden",
              }}>
                {searchResults.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => addAbsent(m)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      width: "100%",
                      padding: "10px 14px",
                      textAlign: "left",
                      background: "transparent",
                      borderBottom: `1px solid ${colors.border}`,
                      transition: "background 120ms",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = colors.goldDim; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  >
                    <Avatar name={`${m.prenom} ${m.nom}`} size="sm" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: colors.text, fontWeight: 500, fontSize: "14px" }}>
                        {m.prenom} {m.nom}
                      </div>
                      <div style={{ color: colors.textMuted, fontSize: "12px" }}>
                        {m.cellule_nom || m.departement || m.role_eglise || "—"}
                      </div>
                    </div>
                    <span style={{ color: colors.gold, fontWeight: 600, fontSize: "13px" }}>+ Absent</span>
                  </button>
                ))}
              </div>
            )}
            {search.trim().length >= 2 && searchResults.length === 0 && (
              <div style={{ marginTop: "10px", color: colors.textMuted, fontSize: "13px", padding: "8px 14px" }}>
                Aucun membre trouvé.
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Liste des absents */}
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <h3 style={{ fontFamily: fonts.heading, fontSize: "20px", fontWeight: 600 }}>
            Absents signalés ({absents.length})
          </h3>
          {absents.length > 0 && (
            <Button onClick={handleSubmit} loading={submitting}>
              Valider {absents.length} absence{absents.length > 1 ? "s" : ""}
            </Button>
          )}
        </div>

        {absents.length === 0 ? (
          <div style={{
            textAlign: "center", padding: "32px", color: colors.textMuted, fontSize: "14px",
            background: colors.bg, borderRadius: "10px", border: `1px dashed ${colors.border}`,
          }}>
            {egliseId
              ? "Recherchez un membre ci-dessus pour le signaler absent."
              : "Sélectionnez d'abord une église."}
          </div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {absents.map((m) => (
              <div
                key={m.id}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "8px 12px",
                  background: colors.bg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "999px",
                  fontSize: "13px",
                }}
              >
                <Avatar name={`${m.prenom} ${m.nom}`} size="xs" />
                <span style={{ color: colors.text, fontWeight: 500 }}>{m.prenom} {m.nom}</span>
                <button
                  onClick={() => removeAbsent(m.id)}
                  aria-label="Retirer"
                  style={{ color: colors.textMuted, fontSize: "16px", padding: "0 4px", lineHeight: 1 }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// ONGLET 2 — PAR CELLULE
// ════════════════════════════════════════════════════════════

function TabCellules() {
  const toast = useToast();
  const { eglises } = useMembers();
  const [cellules, setCellules] = useState([]);   // [{id, nom, eglise_id, eglise_nom, nb_membres}]
  const [selected, setSelected] = useState(null); // cellule complète sélectionnée
  const [members, setMembers] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);

  const loadCellules = useCallback(() => {
    setLoading(true);
    cellulesApi.list()
      .then(({ data }) => setCellules(data || []))
      .catch((err) => toast.error(err.response?.data?.detail || "Erreur de chargement"))
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => { loadCellules(); }, [loadCellules]);

  const openCellule = async (cellule) => {
    setSelected(cellule);
    setStatuses({});
    try {
      const { data } = await cellulesApi.membres(cellule.id);
      setMembers(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
      setMembers([]);
    }
  };

  const handleCreateCellule = async (payload) => {
    try {
      const { data: created } = await cellulesApi.create(payload);
      toast.success(`Cellule « ${created.nom} » créée`);
      setFormOpen(false);
      loadCellules();
      return { ok: true };
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur de création";
      toast.error(msg);
      return { ok: false, error: msg };
    }
  };

  const setStatus = (id, value) => {
    setStatuses((prev) => ({ ...prev, [id]: prev[id] === value ? null : value }));
  };

  const counts = useMemo(() => {
    let p = 0, a = 0;
    Object.values(statuses).forEach((v) => {
      if (v === "present") p++;
      else if (v === "absent") a++;
    });
    return { presents: p, absents: a, restant: members.length - p - a };
  }, [statuses, members]);

  const handleSubmit = async () => {
    if (!members.length) return;
    const eglise_id = members[0].eglise_id;
    const presents = Object.entries(statuses).filter(([, v]) => v === "present").map(([k]) => Number(k));
    const absents  = Object.entries(statuses).filter(([, v]) => v === "absent").map(([k]) => Number(k));

    if (presents.length === 0 && absents.length === 0) {
      toast.info("Marquez au moins un membre");
      return;
    }

    setSubmitting(true);
    try {
      await presencesApi.marquerCellule({
        date_culte: date,
        eglise_id,
        presents,
        absents,
      });
      toast.success(`Cellule « ${selected?.nom} » validée — ${presents.length} présent(s), ${absents.length} absent(s)`);
      setStatuses({});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div style={{ color: colors.textMuted }}>Chargement des cellules...</div>;
  }

  if (cellules.length === 0) {
    return (
      <>
        <Card style={{ textAlign: "center", padding: "60px" }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>👥</div>
          <h3 style={{ fontFamily: fonts.heading, fontSize: "22px" }}>Aucune cellule</h3>
          <p style={{ color: colors.textMuted, marginTop: "8px" }}>
            Créez votre première cellule pour pointer ses membres.
          </p>
          <div style={{ marginTop: "16px" }}>
            <Button onClick={() => setFormOpen(true)}>+ Nouvelle cellule</Button>
          </div>
        </Card>
        <CelluleForm
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSubmit={handleCreateCellule}
          eglises={eglises}
        />
      </>
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: "16px" }} className="cellules-grid">
      {/* Liste cellules */}
      <Card style={{ padding: "8px", height: "fit-content" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px" }}>
          <span style={{ fontSize: "11px", color: colors.gold, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 700 }}>
            Cellules ({cellules.length})
          </span>
          <button
            onClick={() => setFormOpen(true)}
            title="Nouvelle cellule"
            style={{
              color: colors.gold,
              fontSize: "18px",
              padding: "0 6px",
              lineHeight: 1,
              fontWeight: 700,
            }}
          >
            +
          </button>
        </div>
        {cellules.map((c) => (
          <button
            key={c.id}
            onClick={() => openCellule(c)}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              padding: "10px 14px",
              borderRadius: "8px",
              color: selected?.id === c.id ? colors.gold : colors.text,
              background: selected?.id === c.id ? colors.goldDim : "transparent",
              border: `1px solid ${selected?.id === c.id ? colors.goldBorder : "transparent"}`,
              fontSize: "14px",
              fontWeight: selected?.id === c.id ? 600 : 500,
              marginBottom: "2px",
              textAlign: "left",
            }}
          >
            <span>{c.nom}</span>
            <Badge variant={selected?.id === c.id ? "gold" : "default"} size="sm">
              {c.nb_membres}
            </Badge>
          </button>
        ))}
      </Card>

      {/* Détail cellule */}
      <div>
        {!selected ? (
          <Card style={{ textAlign: "center", padding: "60px" }}>
            <div style={{ fontSize: "48px", marginBottom: "12px" }}>👈</div>
            <h3 style={{ fontFamily: fonts.heading, fontSize: "22px" }}>
              Sélectionnez une cellule
            </h3>
            <p style={{ color: colors.textMuted, marginTop: "8px" }}>
              Choisissez une cellule à gauche pour pointer ses membres.
            </p>
          </Card>
        ) : (
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px", marginBottom: "16px" }}>
              <div>
                <h3 style={{ fontFamily: fonts.heading, fontSize: "22px", fontWeight: 600 }}>
                  {selected.nom}
                </h3>
                <div style={{ color: colors.textMuted, fontSize: "13px", marginTop: "2px" }}>
                  ✓ {counts.presents} présent{counts.presents > 1 ? "s" : ""} · ✕ {counts.absents} absent{counts.absents > 1 ? "s" : ""} · {counts.restant} non marqué{counts.restant > 1 ? "s" : ""}
                </div>
              </div>
              <Input
                type="date" value={date}
                onChange={(e) => setDate(e.target.value)}
                fullWidth={false}
                style={{ minWidth: 150 }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {members.map((m) => {
                const status = statuses[m.id];
                return (
                  <div
                    key={m.id}
                    style={{
                      display: "flex", alignItems: "center", gap: "12px",
                      padding: "10px 12px",
                      background: colors.bg,
                      borderRadius: "10px",
                      border: `1px solid ${colors.border}`,
                    }}
                  >
                    <Avatar name={`${m.prenom} ${m.nom}`} size="sm" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500 }}>{m.prenom} {m.nom}</div>
                      {m.role_eglise && (
                        <div style={{ color: colors.textMuted, fontSize: "12px" }}>
                          {m.role_eglise}
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button
                        onClick={() => setStatus(m.id, "present")}
                        style={statusBtn(status === "present", colors.green, "rgba(45,212,160,0.12)")}
                      >
                        ✓ Présent
                      </button>
                      <button
                        onClick={() => setStatus(m.id, "absent")}
                        style={statusBtn(status === "absent", colors.rose, "rgba(224,91,106,0.12)")}
                      >
                        ✕ Absent
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: "16px", display: "flex", justifyContent: "flex-end" }}>
              <Button onClick={handleSubmit} loading={submitting} disabled={counts.presents + counts.absents === 0}>
                Valider la cellule
              </Button>
            </div>
          </Card>
        )}
      </div>

      <style>{`
        @media (max-width: 768px) {
          .cellules-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <CelluleForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleCreateCellule}
        eglises={eglises}
      />
    </div>
  );
}

function statusBtn(active, accent, accentBg) {
  return {
    padding: "8px 12px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: 600,
    color: active ? accent : colors.textMuted,
    background: active ? accentBg : "transparent",
    border: `1px solid ${active ? accent : colors.border}`,
    transition: "all 150ms",
    minWidth: 90,
  };
}

// ════════════════════════════════════════════════════════════
// ONGLET 3 — ALERTES PASTORALES
// ════════════════════════════════════════════════════════════

function TabAlertes() {
  const navigate = useNavigate();
  const toast = useToast();
  const [data, setData] = useState({ rouge: [], orange: [], jaune: [], total_alertes: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await presencesApi.alertes();
      setData(res);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const all = [...data.rouge, ...data.orange, ...data.jaune];
    if (all.length === 0) {
      toast.info("Aucune alerte à exporter");
      return;
    }
    const header = "Niveau;Nom;Telephone;Email;Cellule;Derniere presence;Jours absent\n";
    const rows = all.map((a) => [
      a.niveau, `${a.prenom} ${a.nom}`, a.telephone || "",
      a.email || "", a.cellule || "", a.derniere_presence || "",
      a.jours_absent,
    ].join(";")).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `alertes-pastorales-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`${all.length} alertes exportées`);
  };

  if (loading) {
    return <div style={{ color: colors.textMuted }}>Chargement des alertes...</div>;
  }

  if (data.total_alertes === 0) {
    return (
      <Card style={{ textAlign: "center", padding: "60px" }}>
        <div style={{ fontSize: "48px", marginBottom: "12px" }}>🌟</div>
        <h3 style={{ fontFamily: fonts.heading, fontSize: "22px" }}>Aucune alerte</h3>
        <p style={{ color: colors.textMuted, marginTop: "8px" }}>
          Tous les membres avec un historique sont assidus.
        </p>
      </Card>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
        <div style={{ color: colors.textMuted, fontSize: "14px" }}>
          {data.total_alertes} membre{data.total_alertes > 1 ? "s" : ""} en alerte
          {data.total_critiques > 0 && (
            <> · <span style={{ color: colors.rose, fontWeight: 600 }}>{data.total_critiques} critique{data.total_critiques > 1 ? "s" : ""}</span></>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={exportCsv}>
          ⬇ Exporter la liste
        </Button>
      </div>

      <AlertSection
        title="Absents depuis plus d'1 mois"
        emoji="🔴"
        accent={colors.rose}
        bg="rgba(224,91,106,0.08)"
        members={data.rouge}
        navigate={navigate}
      />
      <AlertSection
        title="Absents depuis 3 semaines"
        emoji="🟠"
        accent="#FB923C"
        bg="rgba(251,146,60,0.08)"
        members={data.orange}
        navigate={navigate}
      />
      <AlertSection
        title="Absents depuis 2 semaines"
        emoji="🟡"
        accent={colors.gold}
        bg={colors.goldDim}
        members={data.jaune}
        navigate={navigate}
      />
    </div>
  );
}

function AlertSection({ title, emoji, accent, bg, members, navigate }) {
  if (members.length === 0) return null;

  return (
    <Card style={{ background: bg, border: `1px solid ${accent}40` }}>
      <h3 style={{
        fontFamily: fonts.heading, fontSize: "20px", fontWeight: 600,
        color: accent, marginBottom: "16px",
        display: "flex", alignItems: "center", gap: "10px",
      }}>
        <span style={{ fontSize: "22px" }}>{emoji}</span>
        {title} <Badge variant="default" size="sm">{members.length}</Badge>
      </h3>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {members.map((m) => (
          <div
            key={m.id}
            style={{
              display: "flex", alignItems: "center", gap: "12px",
              padding: "12px",
              background: colors.bg, borderRadius: "10px",
              border: `1px solid ${colors.border}`,
              flexWrap: "wrap",
            }}
          >
            <Avatar name={m.full_name} size="md" bordered />
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontWeight: 600, color: colors.text }}>{m.full_name}</div>
              <div style={{ color: colors.textMuted, fontSize: "12px", marginTop: "2px" }}>
                Dernière présence : {m.derniere_presence ? new Date(m.derniere_presence).toLocaleDateString("fr-FR") : "—"}
                {" · "}
                <span style={{ color: accent, fontWeight: 600 }}>
                  {Math.floor(m.jours_absent / 7)} semaine{m.jours_absent >= 14 ? "s" : ""} ({m.jours_absent}j)
                </span>
                {m.cellule && <> · {m.cellule}</>}
              </div>
            </div>
            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
              <Button size="sm" variant="ghost" onClick={() => navigate("/annuaire")}>
                Voir fiche
              </Button>
              {m.telephone && (
                <a href={`tel:${m.telephone.replace(/\s/g, "")}`} style={{ textDecoration: "none" }}>
                  <Button size="sm" variant="ghost">📞 Appeler</Button>
                </a>
              )}
              {m.email && (
                <a href={`mailto:${m.email}?subject=${encodeURIComponent("Nouvelles de votre église")}`} style={{ textDecoration: "none" }}>
                  <Button size="sm" variant="ghost">✉ Email</Button>
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
